import { Component, inject, Input, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ContactView, EntityCustomFieldValueView } from '@api-proxies';
import { EntityCardSection } from '@enums/EntityCardSection';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { EntityTypeCustomFieldService } from '@http/clients/entity-type-custom-field.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { customFieldPlacement, ICardConfig } from '@utils/custom-field-placement';
import { GenericCardComponent, ObjectExtensionsService, ValidatorType } from 'ngx-lib';

@Component({
    selector: 'app-contacts-single-overview',
    templateUrl: './contacts-single-overview.component.html',
    styleUrls: ['./contacts-single-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericCardComponent]
})
export class ContactsSingleOverviewComponent implements OnChanges {
    @ViewChild('mainInfoCard')
    mainInfoCard?: GenericCardComponent;

    @ViewChild('customInfoCard')
    customInfoCard?: GenericCardComponent;

    @Input()
    contact?: ContactView;

    @Input()
    customFields?: EntityCustomFieldValueView[];

    @Input()
    isReadOnly: boolean;

    factory: AppEditorFactory;

    entityCardSection: typeof EntityCardSection;
    cardsConfig: ICardConfig;

    companyHasArchived = false;

    private readonly entityCustomFieldService = inject(EntityTypeCustomFieldService);
    private readonly objectExtensionService = inject(ObjectExtensionsService);

    constructor() {
        this.factory = new AppEditorFactory();
        this.isReadOnly = true;
        this.cardsConfig = [];
        this.entityCardSection = EntityCardSection;
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (
            this.contact &&
            changes['contact'] &&
            !this.objectExtensionService.isEqualTo(changes['contact'].currentValue, changes['contact'].previousValue)
        ) {
            this.cardsConfig = {
                [EntityCardSection.ContactMainInfo]: [
                    {
                        label: 'First Name',
                        componentId: EditorType.textInput,
                        fieldName: 'firstName',
                        bindContext: this.contact,
                        isReadOnly: this.isReadOnly,
                        validations: [
                            { type: ValidatorType.required },
                            { type: ValidatorType.string, args: { maxLength: 100 } }
                        ],
                        additionalInfo: {
                            shouldTrimSpaces: true
                        }
                    },
                    {
                        label: 'Last Name',
                        componentId: EditorType.textInput,
                        fieldName: 'lastName',
                        bindContext: this.contact,
                        isReadOnly: this.isReadOnly,
                        validations: [
                            { type: ValidatorType.required },
                            { type: ValidatorType.string, args: { maxLength: 100 } }
                        ],
                        additionalInfo: {
                            shouldTrimSpaces: true
                        }
                    },
                    {
                        label: 'Linkedin',
                        componentId: EditorType.link,
                        fieldName: 'linkedin',
                        bindContext: this.contact,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
                    },
                    {
                        label: 'E-mail',
                        componentId: EditorType.multiInput,
                        fieldName: 'email',
                        bindContext: this.contact,
                        isReadOnly: this.isReadOnly,
                        validations: [
                            { type: ValidatorType.email },
                            { type: ValidatorType.string, args: { maxLength: 200 } }
                        ],
                        additionalInfo: {
                            shouldTrimSpaces: true
                        }
                    },
                    {
                        label: 'Phone',
                        componentId: EditorType.multiInput,
                        fieldName: 'phone',
                        bindContext: this.contact,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 20 } }]
                    },
                    {
                        label: 'Address',
                        componentId: EditorType.multiInput,
                        fieldName: 'address',
                        bindContext: this.contact,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
                    },
                    {
                        label: 'Description',
                        componentId: EditorType.textarea,
                        fieldName: 'description',
                        bindContext: this.contact,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 1000 } }]
                    }
                ],
                [EntityCardSection.ContactCompanyInfo]: [
                    {
                        label: 'Company',
                        componentId: EditorType.company,
                        fieldName: 'companyId',
                        bindContext: this.contact,
                        isReadOnly: this.isReadOnly,
                        // this property 'additionalInfo: { allowAction: true }' must be set if we want to allow the user to create a new company by opening quick add modal
                        additionalInfo: {
                            allowAction: true,
                            hasMainContactSet: !!this.contact?.isMain,
                            isActive: true,
                            allowToNavigate: true
                        }
                    },
                    {
                        label: 'Position',
                        componentId: EditorType.textInput,
                        fieldName: 'position',
                        bindContext: this.contact,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
                    }
                ],
                [EntityCardSection.ContactCustomInfo]: []
            };

            if (!this.contact.customFieldValues?.length)
                this.contact.customFieldValues = await this.entityCustomFieldService.getCustomFieldsByEntityType(
                    EntityTypes.Contact
                );

            this.contact.customFieldValues.forEach((customField) => {
                customFieldPlacement(
                    this.cardsConfig,
                    customField,
                    EntityCardSection.ContactCustomInfo,
                    this.isReadOnly
                );
            });
            await this.checkArchivedItems(this.contact);
        }
    }

    validate(): boolean {
        const mainInfoCardValid = this.mainInfoCard ? this.mainInfoCard.validate() : false;
        const customInfoCard = this.customInfoCard ? this.customInfoCard.validate() : true; // if no custom fields: set as valid

        const allValid = mainInfoCardValid && customInfoCard;

        return allValid;
    }

    private async checkArchivedItems(contact: ContactView): Promise<void> {
        this.companyHasArchived = contact.companyIsActive === false;
    }
}
